// src/components/course/pages/CourseDashboard.jsx
import React, { useEffect, useState } from 'react';
import { 
  TrendingUp, Clock, BookOpen, Trophy, ChevronRight, Play, Star, Users, Calendar,
  Target, Zap, Award, ArrowUpRight, BarChart3, Plus, CheckCircle, Timer, Brain,
  Sparkles, Flame, Search, Video
} from 'lucide-react';
import { courseService } from '../../../services/courseService';
import { motion } from 'framer-motion';

const CourseDashboard = ({ bookId, user, userProfile, onNavigate }) => {
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [continueCourses, setContinueCourses] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [todayStats, setTodayStats] = useState({
    minutesLearned: 25,
    lessonsCompleted: 3,
    streakDays: userProfile?.streak || 0,
    xpEarned: 45
  });
  const [weeklyGoal] = useState(300);
  const [weeklyProgress, setWeeklyProgress] = useState(180);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      const featured = await courseService.getCourses({ 
        bookId,
        featured: true, 
        limit: 8 
      });
      setFeaturedCourses(featured);
      
      setContinueCourses([
        {
          id: '1',
          title: 'Advanced React Development',
          category: 'Web Development',
          progress: 75,
          totalLessons: 12,
          completedLessons: 9,
          thumbnail: null,
          estimatedTime: '2h 30m'
        },
        {
          id: '2',
          title: 'Digital Marketing Essentials',
          category: 'Marketing',
          progress: 40,
          totalLessons: 8,
          completedLessons: 3,
          thumbnail: null,
          estimatedTime: '4h 15m'
        }
      ]);

      setRecentActivity([
        { 
          type: 'lesson_completed', 
          title: 'Completed "React Hooks"', 
          time: '2 hours ago', 
          xp: 15,
          icon: CheckCircle,
          color: 'green'
        },
        { 
          type: 'course_started', 
          title: 'Started "Digital Marketing"', 
          time: '1 day ago', 
          xp: 10,
          icon: Play,
          color: 'blue'
        }
      ]);

      if (user) {
        const sessions = await courseService.getUpcomingSessions(bookId, user.uid);
        setUpcomingSessions(sessions || []);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const weeklyProgressPercentage = Math.min((weeklyProgress / weeklyGoal) * 100, 100)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome back, {userProfile?.name || user?.displayName || 'Learner'}! 👋
        </h1>
        <p className="text-gray-600">Continue your learning journey</p>
      </motion.div>

      {/* Today's Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={Timer}
          value={`${todayStats.minutesLearned}m`}
          label="Learned Today"
          color="blue"
        />
        <StatCard
          icon={CheckCircle}
          value={todayStats.lessonsCompleted}
          label="Lessons Done"
          color="green"
        />
        <StatCard
          icon={Flame}
          value={`${todayStats.streakDays} days`}
          label="Current Streak"
          color="orange"
        />
        <StatCard
          icon={Sparkles}
          value={`${todayStats.xpEarned} XP`}
          label="XP Earned"
          color="purple"
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-gradient-to-br from-[#190192] via-purple-500 to-pink-500 rounded-2xl p-6 lg:p-8 text-white relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-32 -mt-32"></div>
        
        <div className="relative">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1">
              <h3 className="text-xl lg:text-2xl font-bold mb-2">Weekly Learning Progress</h3>
              <p className="text-indigo-100 mb-6">
                {Math.floor(weeklyProgress / 60)}h {weeklyProgress % 60}m of {Math.floor(weeklyGoal / 60)}h goal completed this week
              </p>
              
              <div className="space-y-4">
                <div className="w-full bg-white/20 rounded-full h-3">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${weeklyProgressPercentage}%` }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="bg-white rounded-full h-3 flex items-center justify-end pr-2"
                  >
                    <Zap className="w-3 h-3 text-indigo-500" />
                  </motion.div>
                </div>
                
                <div className="grid grid-cols-7 gap-1">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => (
                    <div key={day} className="text-center">
                      <div className={`w-8 h-8 rounded-full mb-1 ${
                        index < 4 ? 'bg-white/30' : 'bg-white/10'
                      } flex items-center justify-center`}>
                        <div className={`w-3 h-3 rounded-full ${
                          index < 4 ? 'bg-white' : 'bg-white/30'
                        }`}></div>
                      </div>
                      <span className="text-xs text-indigo-200">{day}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="text-center lg:text-right">
              <div className="text-4xl lg:text-5xl font-bold mb-2">{Math.round(weeklyProgressPercentage)}%</div>
              <div className="text-indigo-200">Complete</div>
              <div className="mt-4 text-sm text-indigo-100">
                {weeklyGoal - weeklyProgress > 0 
                  ? `${Math.floor((weeklyGoal - weeklyProgress) / 60)}h ${(weeklyGoal - weeklyProgress) % 60}m to goal`
                  : '🎉 Goal achieved!'
                }
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Quick Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-8">
        <button
          onClick={() => onNavigate('my-learning')}
          className="bg-blue-500 rounded-2xl p-6 text-white hover:bg-blue-600 transition-colors text-left"
        >
          <div className="flex items-center justify-between mb-4">
            <Play className="w-8 h-8" />
            <span className="bg-white bg-opacity-20 px-2 py-1 rounded-full text-sm">
              {continueCourses.length}
            </span>
          </div>
          <h3 className="text-xl font-bold mb-2">Continue Learning</h3>
          <p className="text-blue-100">Pick up where you left off</p>
        </button>

        <button
          onClick={() => onNavigate('discover')}
          className="bg-green-500 rounded-2xl p-6 text-white hover:bg-green-600 transition-colors text-left"
        >
          <div className="flex items-center justify-between mb-4">
            <Search className="w-8 h-8" />
            <span className="bg-white bg-opacity-20 px-2 py-1 rounded-full text-sm">50+</span>
          </div>
          <h3 className="text-xl font-bold mb-2">Discover New</h3>
          <p className="text-green-100">Find courses to expand skills</p>
        </button>

        <button
          onClick={() => onNavigate('xp')}
          className="bg-purple-500 rounded-2xl p-6 text-white hover:bg-purple-600 transition-colors text-left"
        >
          <div className="flex items-center justify-between mb-4">
            <BarChart3 className="w-8 h-8" />
            <span className="bg-white bg-opacity-20 px-2 py-1 rounded-full text-sm">
              {userProfile?.achievements?.length || 0}
            </span>
          </div>
          <h3 className="text-xl font-bold mb-2">Track Progress</h3>
          <p className="text-purple-100">View achievements & stats</p>
        </button>
      </div>

      {/* Continue Learning + Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Continue Learning Section */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Continue Learning</h2>
            <button
              onClick={() => onNavigate('my-learning')}
              className="text-blue-500 hover:text-blue-600 font-medium flex items-center space-x-1"
            >
              <span>View all</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-4">
            {continueCourses.map((course) => (
              <ContinueCourseCard
                key={course.id}
                course={course}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Recent Activity</h2>
          <div className="space-y-3">
            {recentActivity.map((activity, index) => (
              <ActivityItem key={index} activity={activity} />
            ))}
          </div>
        </div>
      </div>

      {/* Upcoming Sessions (if any) */}
      {upcomingSessions.length > 0 && (
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Upcoming Sessions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {upcomingSessions.map((session) => (
              <SessionCard key={session.id} session={session} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper Components
const StatCard = ({ icon: Icon, value, label, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    purple: 'bg-purple-50 text-purple-600'
  };

  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200">
      <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-600">{label}</p>
    </div>
  );
};

const ContinueCourseCard = ({ course, onNavigate }) => (
  <button
    onClick={() => onNavigate('course', { courseId: course.id })}
    className="w-full bg-white rounded-xl p-4 border border-gray-200 hover:border-blue-500 hover:shadow-md transition-all text-left"
  >
    <div className="flex items-center gap-4">
      <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
        <BookOpen className="w-8 h-8 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-gray-900 mb-1 truncate">{course.title}</h3>
        <p className="text-sm text-gray-600 mb-2">{course.category}</p>
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span>{course.completedLessons}/{course.totalLessons} lessons</span>
          <span>{course.estimatedTime} left</span>
        </div>
      </div>
      <div className="flex flex-col items-end">
        <div className="text-2xl font-bold text-blue-600 mb-1">{course.progress}%</div>
        <div className="w-20 bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full"
            style={{ width: `${course.progress}%` }}
          />
        </div>
      </div>
    </div>
  </button>
);

const ActivityItem = ({ activity }) => {
  const colorClasses = {
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600'
  };

  return (
    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
      <div className={`w-8 h-8 rounded-lg ${colorClasses[activity.color]} flex items-center justify-center flex-shrink-0`}>
        <activity.icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">{activity.title}</p>
        <p className="text-xs text-gray-500">{activity.time}</p>
      </div>
      <span className="text-xs font-semibold text-purple-600">+{activity.xp} XP</span>
    </div>
  );
};

const SessionCard = ({ session }) => (
  <div className="bg-white rounded-xl p-4 border border-gray-200">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
        <Video className="w-5 h-5 text-blue-600" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-gray-900 truncate">{session.title}</h4>
        <p className="text-sm text-gray-600">{session.instructor}</p>
      </div>
    </div>
    <div className="flex items-center gap-2 text-sm text-gray-600">
      <Calendar className="w-4 h-4" />
      <span>{session.date} at {session.time}</span>
    </div>
  </div>
);

export default CourseDashboard;